// client/src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { scrapingService } from '../services/scrapingService';

// Create the context
const AuthContext = createContext();

// Config parameter to easily toggle auth requirement
const AUTH_CONFIG = {
  requireAuth: true,      // Set to false to bypass authentication
  sessionDuration: 48,    // Session duration in hours
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      // Skip auth check if not required
      if (!AUTH_CONFIG.requireAuth) {
        setCurrentUser({ bypass: true });
        setLoading(false);
        return;
      }

      try {
        const response = await scrapingService.checkSession();
        if (response.authenticated) {
          setCurrentUser(response.user);
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Session check failed:", error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  // Login function
  const login = async (email, password) => {
    // Skip actual login if auth not required
    if (!AUTH_CONFIG.requireAuth) {
      setCurrentUser({ bypass: true });
      return { success: true };
    }

    const response = await scrapingService.login(email, password);
    
    if (response.success) {
      setCurrentUser(response.user);
      return response;
    } else {
      throw new Error(response.error || 'Login failed');
    }
  };

  // Logout function
  const logout = async () => {
    if (!AUTH_CONFIG.requireAuth) {
      setCurrentUser(null);
      return { success: true };
    }

    try {
      await scrapingService.logout();
      setCurrentUser(null);
      return { success: true };
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  };

  const value = {
    currentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    authRequired: AUTH_CONFIG.requireAuth,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook for easy context use
export function useAuth() {
  return useContext(AuthContext);
}